import { useState, useEffect, useCallback } from 'react';
import { GetAlertsCustomizationByShipmentId } from '../apis/settings';
import useUnauthorize from './useUnauthorize';
import { useToken } from '../common/helper';
import type { AlertThreshold } from '../pages/Shipments/PerShipmentNotification/components/AlertFieldByType';

interface UseFetchAlertThresholdsReturn {
  alertThreshold: AlertThreshold[];
  isLoading: boolean;
  refetchAlertThreshold: () => void;
}

// One request is shared by every component mounted at the same time (the Home
// Alerts and Alert workload filters both mount together). Before, the second
// one returned early and never received the alert types.
let inFlight: Promise<AlertThreshold[]> | null = null;

const useFetchAlertThresholds = (
  teamId: string | null,
  skip = false // Add skip parameter to prevent API call when override is provided
): UseFetchAlertThresholdsReturn => {
  const { checkTokenExpiry } = useToken();
  const { unauthorize } = useUnauthorize();
  const [alertThreshold, setAlertThreshold] = useState<AlertThreshold[]>([]);
  const [isLoading, setIsLoading] = useState(!skip); // Don't show loading if skipped

  const requestThresholds = useCallback((): Promise<AlertThreshold[]> => {
    const token = checkTokenExpiry();
    if (!token || skip) {
      return Promise.resolve([]);
    }
    return GetAlertsCustomizationByShipmentId(token, teamId ?? undefined)
      .then((res): AlertThreshold[] => {
        const responseData = res?.data;
        if (
          responseData?.data?.is_success &&
          Array.isArray(responseData?.data?.data)
        ) {
          // The API item type and the filter's AlertThreshold describe the same
          // payload with different field sets; unifying them is future work.
          // eslint-disable-next-line no-restricted-syntax
          return responseData.data.data as unknown as AlertThreshold[];
        }
        return [];
      })
      .catch((err): AlertThreshold[] => {
        unauthorize(err);
        return [];
      });
  }, [checkTokenExpiry, teamId, unauthorize, skip]);

  const fetchAlertThreshold = useCallback(
    () => requestThresholds().then(setAlertThreshold),
    [requestThresholds]
  );

  useEffect(() => {
    if (skip) {
      setIsLoading(false);
      return;
    }
    let active = true;
    const request =
      inFlight ??
      requestThresholds().finally(() => {
        inFlight = null;
      });
    inFlight = request;
    const _loaded = request.then((data) => {
      if (!active) return;
      setAlertThreshold(data);
      setIsLoading(false);
    });
    return () => {
      active = false;
    };
  }, [requestThresholds, skip]);

  return {
    alertThreshold,
    isLoading,
    refetchAlertThreshold: fetchAlertThreshold,
  };
};

export default useFetchAlertThresholds;
